import React, { useEffect, useRef } from "react";
import Calendar from "@amzn/awsui-components-core/calendar";
import Icon from "@amzn/awsui-components-core/icon";

// Marks the "Custom date" option's content so the Time Range field can tell
// when that option is clicked or activated from the keyboard.
export const CUSTOM_DATE_OPTION_ATTRIBUTE = "data-custom-date-option";

const isPastOrToday = (date: Date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date <= today;
};

interface TimeRangeCustomDateOptionProps {
  // Selected custom date in YYYY-MM-DD format, or "" when none.
  value: string;
}

/**
 * Content of the "Custom date" option, the last item in the Time Range list.
 * It is a real option so the list highlights it like the presets above it.
 */
export function TimeRangeCustomDateOption({
  value,
}: TimeRangeCustomDateOptionProps) {
  return (
    <span
      className="time-range-custom-date-option"
      data-testid="custom-date-toggle"
      {...{ [CUSTOM_DATE_OPTION_ATTRIBUTE]: "" }}
    >
      <span>Custom date</span>
      <span className="time-range-custom-date-value">
        {value ? value.replace(/-/g, "/") : null}
        <Icon name="angle-right" size="small" />
      </span>
    </span>
  );
}

interface TimeRangeCustomDateFooterProps {
  // Selected custom date in YYYY-MM-DD format, or "" when none.
  value: string;
  onChange: (date: string) => void;
  // Returns to the presets.
  onBack: () => void;
}

/**
 * Calendar that replaces the Time Range presets after "Custom date" is picked,
 * so the dropdown keeps its size. "‹ Custom date" goes back to the presets.
 */
export function TimeRangeCustomDateFooter({
  value,
  onChange,
  onBack,
}: TimeRangeCustomDateFooterProps) {
  // The footer unmounts when the dropdown closes; reopen on the presets.
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;
  useEffect(() => () => onBackRef.current(), []);

  return (
    // The dropdown prevents default on mousedown to keep focus on its trigger,
    // which would stop the calendar from taking keyboard focus on click.
    <div
      className="time-range-custom-date"
      data-testid="custom-date-footer"
      role="presentation"
      onMouseDown={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        className="time-range-custom-date-row"
        data-testid="custom-date-back"
        onClick={onBack}
      >
        <span className="time-range-custom-date-label">
          <Icon name="angle-left" size="small" />
          Custom date
        </span>
      </button>
      <div className="time-range-custom-date-picker">
        <Calendar
          value={value}
          onChange={({ detail }) => onChange(detail.value)}
          isDateEnabled={isPastOrToday}
          ariaLabel="Custom date"
          i18nStrings={{
            todayAriaLabel: "Today",
            nextMonthAriaLabel: "Next month",
            previousMonthAriaLabel: "Previous month",
          }}
        />
        <div className="time-range-custom-date-hint">
          Searches back from 12:00 UTC.
        </div>
      </div>
    </div>
  );
}
